import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChatHeader } from './components/ChatHeader';
import { MessageList } from './components/MessageList';
import { InputBar } from './components/InputBar';
import { TypingIndicator } from './components/TypingIndicator';
import { OfflineView } from './components/OfflineView';
import { IdentityGate } from './components/IdentityGate';
import { useConfig } from './hooks/useConfig';
import { useSession } from './hooks/useSession';
import { useWebSocket } from './hooks/useWebSocket';
import { usePostMessage } from './hooks/usePostMessage';
import { useTranslation } from './hooks/useTranslation';
import type { DisplayMessage, WidgetState, WidgetMode, WidgetMessage, WidgetServerEvent, BusinessHours } from './types';

function extractTenantFromPath(): string | null {
  // URL pattern: /chat/{tenant}
  const match = window.location.pathname.match(/^\/chat\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function getChannelFromSearch(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get('channel') || 'website-chat';
}

function isWithinBusinessHours(bh: BusinessHours | null): boolean {
  if (!bh) return true; // No business hours configured = always open

  try {
    const now = new Date();
    // Get current day in the configured timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      timeZone: bh.timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(now);
    const dayName = parts.find((p) => p.type === 'weekday')?.value?.toLowerCase() || '';
    const hour = parts.find((p) => p.type === 'hour')?.value || '00';
    const minute = parts.find((p) => p.type === 'minute')?.value || '00';
    const currentMinutes = parseInt(hour) * 60 + parseInt(minute);

    const daySchedule = bh.schedule[dayName];
    if (!daySchedule) return false; // Null means closed on this day

    const [startH, startM] = daySchedule.start.split(':').map(Number);
    const [endH, endM] = daySchedule.end.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
  } catch {
    return true; // On error, assume open
  }
}

const App: React.FC = () => {
  const tenant = useMemo(extractTenantFromPath, []);
  const channel = useMemo(getChannelFromSearch, []);

  const widgetMode: WidgetMode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mode') === 'standalone') return 'standalone';
    if (window.parent === window) return 'standalone';
    return 'embedded';
  }, []);

  // Identity gate: check localStorage for customer name + phone
  const [customerIdentity, setCustomerIdentity] = useState<{ name: string; phone: string } | null>(() => {
    if (!tenant) return null;
    const name = localStorage.getItem(`qbrains:${tenant}:customer_name`);
    const phone = localStorage.getItem(`qbrains:${tenant}:customer_phone`);
    return name && phone ? { name, phone } : null;
  });

  const handleIdentityComplete = useCallback((name: string, phone: string) => {
    setCustomerIdentity({ name, phone });
  }, []);

  const { config, loading: configLoading, error: configError } = useConfig(tenant);
  const { pageContext, identity, sendToParent } = usePostMessage(widgetMode);
  const { t } = useTranslation(config?.behavior?.locale || identity?.locale);

  // Build session context from identity + page info + customer identity
  // Always include customerIdentity even if postMessage identity is null (anonymous visitor)
  const sessionContext = useMemo(() => {
    return {
      locale: identity?.locale || undefined,
      url: pageContext?.url,
      page_title: pageContext?.pageTitle,
      user_id: identity?.userId || undefined,
      user_name: customerIdentity?.name || identity?.userName || undefined,
      user_email: identity?.userEmail || undefined,
      user_phone: customerIdentity?.phone || undefined,
    };
  }, [identity, pageContext, customerIdentity]);

  // Wait for identity postMessage or fall back after 200ms for anonymous visitors.
  // ALSO wait for customerIdentity from Identity Gate — session must include name+phone.
  const [sessionReady, setSessionReady] = useState(false);
  useEffect(() => {
    // Don't create session until we have customer identity (from Identity Gate localStorage)
    if (!customerIdentity) {
      setSessionReady(false);
      return;
    }
    if (identity !== null) {
      setSessionReady(true);
      return;
    }
    const timer = setTimeout(() => setSessionReady(true), 200);
    return () => clearTimeout(timer);
  }, [identity, customerIdentity]);

  const { session, history, loading: sessionLoading, error: sessionError, retry: retrySession } = useSession(tenant, sessionContext, sessionReady);

  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [widgetState, setWidgetState] = useState<WidgetState>('idle');
  const [isTyping, setIsTyping] = useState(false);
  const objectUrlsRef = useRef<string[]>([]);

  // Handle server events directly via callback — no intermediate state, no useEffect
  const handleServerEvent = useCallback((event: WidgetServerEvent) => {
    switch (event.type) {
      case 'message': {
        const msg: DisplayMessage = {
          id: crypto.randomUUID(),
          role: event.role === 'agent' ? 'agent' : 'system',
          content: event.content || '',
          timestamp: event.timestamp || new Date().toISOString(),
          parts: event.parts,
        };
        setMessages((prev) => [...prev, msg]);
        setIsTyping(false);
        setWidgetState('active');
        sendToParent({ type: 'qbrains:sound' });
        break;
      }
      case 'typing_start':
        setIsTyping(true);
        break;
      case 'typing_stop':
        setIsTyping(false);
        break;
      case 'escalated':
        setWidgetState('escalated');
        setIsTyping(false);
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'system',
            content: event.message || t('system.escalated'),
            timestamp: new Date().toISOString(),
          },
        ]);
        break;
      case 'agent_joined':
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'system',
            content: t('system.agentJoined', { name: event.name || 'Agent' }),
            timestamp: new Date().toISOString(),
          },
        ]);
        break;
      case 'conversation_ended':
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'system',
            content: event.message || t('system.conversationEnded'),
            timestamp: new Date().toISOString(),
          },
        ]);
        break;
      case 'error':
        setWidgetState('error');
        break;
    }
  }, [sendToParent]);

  const { sendMessage, connected, exhausted, reconnect } = useWebSocket({
    tenant,
    channel,
    sessionToken: session?.session_token || null,
    onEvent: handleServerEvent,
  });

  // Revoke all tracked object URLs on unmount only
  useEffect(() => {
    const urls = objectUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // Initialize messages from history
  useEffect(() => {
    if (history.length > 0) {
      setMessages(history);
    }
  }, [history]);

  // Add welcome message only for genuinely new conversations
  useEffect(() => {
    if (!config || sessionLoading || history.length > 0 || messages.length > 0 || !tenant) return;

    // Check if user has already interacted (session exists in storage)
    const hasExistingSession = !!localStorage.getItem(`qbrains:${tenant}:welcomed`);
    if (hasExistingSession) return;

    const welcomeContent = customerIdentity
      ? `Chao ${customerIdentity.name} (${customerIdentity.phone}), toi co the giup gi cho ban?`
      : config.behavior.welcome_message;

    const welcomeMsg: DisplayMessage = {
      id: 'welcome',
      role: 'agent',
      content: welcomeContent,
      timestamp: new Date().toISOString(),
    };
    setMessages([welcomeMsg]);
    localStorage.setItem(`qbrains:${tenant}:welcomed`, '1');
  }, [config, sessionLoading, history, messages.length, tenant]);

  // Check business hours on load and every 60s
  useEffect(() => {
    if (!config) return;

    const check = () => {
      const online = isWithinBusinessHours(config.behavior.business_hours);
      setWidgetState((prev) => {
        if (!online && prev !== 'offline') return 'offline';
        if (online && prev === 'offline') return 'idle';
        return prev;
      });
    };

    check();
    const interval = setInterval(check, 60_000);
    return () => clearInterval(interval);
  }, [config]);

  // (Server events handled via handleServerEvent callback passed to useWebSocket)

  // Upload a file via the chat upload endpoint, returns file metadata or null on failure
  const uploadFile = useCallback(
    async (file: File): Promise<DisplayMessage['file'] | null> => {
      if (!session?.session_token) return null;
      try {
        const form = new FormData();
        form.append('file', file);
        const apiBase = import.meta.env.VITE_API_URL || '';
        const res = await fetch(`${apiBase}/api/v1/chat/upload`, {
          method: 'POST',
          headers: { 'X-Session-Token': session.session_token },
          body: form,
        });
        if (!res.ok) return null;
        const data = await res.json();
        return {
          file_id: data.file_id,
          url: data.url,
          mime_type: data.mime_type,
          size_bytes: data.size_bytes,
          filename: data.filename,
        };
      } catch {
        return null;
      }
    },
    [session]
  );

  // Handle sending a message
  const handleSend = useCallback(
    async (text: string, file?: File) => {
      if (!text && !file) return;

      // Add customer message to display immediately (optimistic)
      const objectUrl = file ? URL.createObjectURL(file) : undefined;
      if (objectUrl) objectUrlsRef.current.push(objectUrl);

      const customerMsg: DisplayMessage = {
        id: crypto.randomUUID(),
        role: 'customer',
        content: text,
        timestamp: new Date().toISOString(),
        file: file
          ? {
              file_id: 'pending',
              url: objectUrl!,
              mime_type: file.type,
              size_bytes: file.size,
              filename: file.name,
            }
          : undefined,
      };
      setMessages((prev) => [...prev, customerMsg]);
      setWidgetState('active');

      // Upload file first if present
      let uploadedFile: DisplayMessage['file'] | undefined;
      if (file) {
        const result = await uploadFile(file);
        if (result) {
          uploadedFile = result;
          // Update the displayed message with the real URL
          setMessages((prev) =>
            prev.map((m) => (m.id === customerMsg.id ? { ...m, file: result } : m))
          );
        } else {
          // File upload failed — mark message as failed
          setMessages((prev) =>
            prev.map((m) => (m.id === customerMsg.id ? { ...m, status: 'failed' as const } : m))
          );
          return;
        }
      }

      // Send via WebSocket
      const wsMessage: WidgetMessage = {
        type: 'message',
        content: text,
        timestamp: new Date().toISOString(),
        context: pageContext
          ? { url: pageContext.url, page_title: pageContext.pageTitle }
          : undefined,
        ...(uploadedFile
          ? {
              file: {
                file_id: uploadedFile.file_id,
                url: uploadedFile.url,
                mime_type: uploadedFile.mime_type,
                size_bytes: uploadedFile.size_bytes,
                filename: uploadedFile.filename,
              },
            }
          : {}),
      };
      const sent = sendMessage(wsMessage);

      if (!sent) {
        setMessages((prev) =>
          prev.map((m) => (m.id === customerMsg.id ? { ...m, status: 'failed' as const } : m))
        );
      }
    },
    [sendMessage, pageContext, uploadFile]
  );

  // Handle artifact actions (e.g. product selector quote request)
  const handleArtifactAction = useCallback(
    (action: string, data: unknown) => {
      if (action === 'request-quote') {
        const items = data as Array<{ name: string; code: string; quantity: number; unitPrice: number; unit: string }>;
        const itemsList = items
          .map((i) => `${i.quantity} ${i.unit} ${i.name}`)
          .join(', ');
        const text = `[BAO GIA] Toi muon bao gia chinh thuc cho: ${itemsList}`;
        handleSend(text);
      }
    },
    [handleSend]
  );

  // Retry sending a failed message
  const handleRetry = useCallback(
    (messageId: string) => {
      const msg = messages.find((m) => m.id === messageId);
      if (!msg) return;

      const wsMessage: WidgetMessage = {
        type: 'message',
        content: msg.content,
        timestamp: new Date().toISOString(),
        context: pageContext
          ? { url: pageContext.url, page_title: pageContext.pageTitle }
          : undefined,
      };
      const sent = sendMessage(wsMessage);
      if (sent) {
        setMessages((prev) =>
          prev.map((m) => (m.id === messageId ? { ...m, status: 'sent' as const } : m))
        );
      }
    },
    [messages, sendMessage, pageContext]
  );

  const handleClose = useCallback(() => {
    sendToParent({ type: 'qbrains:close' });
  }, [sendToParent]);

  const handleMinimize = useCallback(() => {
    sendToParent({ type: 'qbrains:minimize' });
  }, [sendToParent]);

  // Identity gate: block widget until customer provides name + phone
  if (!customerIdentity) {
    return (
      <IdentityGate
        tenant={tenant || ''}
        brandName={config?.branding?.header_title || 'QBrains'}
        primaryColor={config?.branding?.primary_color}
        onComplete={handleIdentityComplete}
      />
    );
  }

  // Loading state
  if (configLoading || sessionLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-white" role="status" aria-label="Loading chat">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" aria-hidden="true" />
      </div>
    );
  }

  // Error state
  if (configError || !config || sessionError) {
    return (
      <div className="h-full flex items-center justify-center bg-white p-6 text-center">
        <div>
          <p className="text-sm text-gray-500">{t('error.loadWidget')}</p>
          <p className="text-xs text-gray-400 mt-1">{sessionError || configError || t('error.configNotFound')}</p>
          {sessionError && (
            <button
              onClick={retrySession}
              className="mt-3 text-xs font-medium text-blue-600 hover:text-blue-800 underline"
            >
              {t('error.tryAgain')}
            </button>
          )}
        </div>
      </div>
    );
  }

  const isOffline = widgetState === 'offline';

  return (
    <div
      className={`flex flex-col bg-white overflow-hidden ${widgetMode === 'standalone' ? 'h-[100dvh] safe-area-inset' : 'h-full'}`}
      style={{ fontFamily: config.branding.font_family || 'system-ui, sans-serif' }}
    >
      <ChatHeader config={config} widgetMode={widgetMode} onClose={handleClose} onMinimize={handleMinimize} />

      {isOffline ? (
        <OfflineView config={config} />
      ) : (
        <>
          <MessageList messages={messages} config={config} onRetry={handleRetry} onArtifactAction={handleArtifactAction} />
          {isTyping && (
            <TypingIndicator agentBubbleColor={config.branding.agent_bubble_color} />
          )}
          <InputBar config={config} disabled={isOffline} onSend={handleSend} />
        </>
      )}

      {config.branding.show_powered_by && (
        <div className="shrink-0 text-center py-1.5 bg-gray-50 border-t border-gray-100">
          <span className="text-[10px] text-gray-500">
            {t('chat.poweredBy')}{' '}
            <a
              href="https://qbrains.io"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium hover:underline"
            >
              QBrains
            </a>
          </span>
        </div>
      )}

      {!connected && widgetState !== 'offline' && (
        <div className="shrink-0 text-center py-1.5 bg-amber-50 border-t border-amber-100">
          {exhausted ? (
            <span className="text-[11px] text-amber-700">
              {t('connection.lost')}{' '}
              <button
                onClick={reconnect}
                className="underline font-medium hover:text-amber-900"
              >
                {t('connection.tryAgain')}
              </button>
            </span>
          ) : (
            <span className="text-[11px] text-amber-600">{t('connection.reconnecting')}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default App;
